import { ControlGroup } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomCheckbox from "components/Atoms/CustomCheckbox"
import CustomDateInput from "components/Atoms/CustomDateInput"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomSwitch from "components/Atoms/CustomSwitch"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import ButtonMutation from "components/Molecules/ButtonMutation"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import FormController from "components/Molecules/FormController"
import { CURRENT_STOCK_UNITS } from "data/static.items"
import { GROUPS } from "graphql/selector.queries.gql"
import { ADD_UPDATE_SELLABLE_ITEM_STOCK } from "graphql/sellable-item/sellable-item.mutations.gql"
import { useFormDisabled } from "hooks/useAuth"
import useErrors from "hooks/useErrors"
import { cloneDeep, pick, set } from "lodash-es"
import sellableStockModel from "models/sellable-stock.model"
import React, { useEffect, useMemo, useState } from "react"
import { useCurrentDc } from "store/auth.redux"
import AppToaster from "utils/AppToaster"
// import ItemBenchmarkDialog from "./ItemBenchmarkDialog";
import { camelCaseShallow, getId, parseJson } from "utils/custom.utils"
import { graphqlError } from "utils/parseError"
import AlternateStockDropdown from "./AlternateStockDropdown"
import DisplayStockNote from "./DisplayStockNote"
import ItemBenchmarkDialog from "./ItemBenchmarkDialog"
const SellableItemStock = ({
  stock,
  onDelete,
  index,
  sellableItem,
  onCompleted,
}) => {
  const [dc] = useCurrentDc()
  const distributionCentre = dc
  const sellableItemId = sellableItem?.id
  const [isOpen, setOpen] = useState(false)
  const [state, setState] = useState<any>({})
  useEffect(() => {
    // Update the state whenever the props change
    setState({
      isLive: true,
      ...stock,
      orderingConditions: parseJson(stock?.orderingConditions),
      currentStockType:
        CURRENT_STOCK_UNITS.find(
          unit => unit.id === stock.currentStockType?.toLowerCase?.()
        ) || CURRENT_STOCK_UNITS[0],
    })
  }, [stock])

  function handleChange(path, value) {
    const stateCopy = cloneDeep(state)
    set(stateCopy, path, value)
    setState(stateCopy)
  }

  const isReseller = state?.groupVisibility?.find(
    g => g?.category?.codename === "RSL"
  )

  const parsedVariables = useMemo(() => {
    return {
      ...pick(state, [
        "id",
        "currentStockCount",
        "isLive",
        "unitRate",
        "expiryDate",
        "discountedRate",
        "offerStockCount",
      ]),
      ...(state?.currentStockType?.id === "percentage" && {
        currentStockPercentage: state?.currentStockPercentage ?? "",
      }),
      orderingConditions: camelCaseShallow(state?.orderingConditions),
      currentStockType: state?.currentStockType?.id,
      groupVisibility: state?.groupVisibility?.map(getId),
      sellableItemId,
      relatedStockId: state?.relatedStock?.id,
      distributionCentreId: distributionCentre?.id,
      minRate: isReseller ? state?.minRate : undefined,
      maxRate: isReseller ? state.maxRate : undefined,
    }
  }, [state, isReseller, sellableItemId, distributionCentre])

  const [errors] = useErrors({
    state: parsedVariables,
    schema: sellableStockModel,
  })

  const disabled = useFormDisabled("stock", parsedVariables)
  return (
    <>
      <FormController
        disabled={disabled}
        disableAll={true}
        errors={errors?.details}
        source={state}
        onChange={handleChange}
      >
        <div className="vt-sellable-item-stock">
          <div className="flex justify-between p-3">
            <CustomSwitch
              className="vt-stock-live"
              label="Stock Live"
              large
              path="isLive"
            />
            <header className="dc">{distributionCentre?.name}</header>
            <div>
              <CustomButton
                icon="comparison"
                intent="success"
                text="Benchmark Rates"
                minimal
                onClick={e => setOpen(true)}
              />
              <CustomButton
                disabled={stock.id ? true : false}
                onClick={e => onDelete(state, index)}
                icon="trash"
                intent="danger"
                minimal
              />
            </div>
          </div>
          <hr className="my-0" />
          <section>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6">
                <DynamicMultiSelector
                  label="Group Visibility"
                  fill
                  query={GROUPS}
                  variables={{ filter: { categories: ["RTL", "RSL"] } }}
                  path="groupVisibility"
                  popoverClassName="sk-group-visibility-popover"
                  renderLabel={item => {
                    return item?.category.name
                  }}
                />
              </div>

              <div className="col-span-3">
                <StringInput
                  leftIcon="rupee"
                  label="Unit Rate"
                  path="unitRate"
                  big={false}
                />
              </div>
              <div className="col-span-3">
                <StringInput
                  leftIcon="rupee"
                  label="Discounted Rate"
                  path="discountedRate"
                  helperText="Unitrate - Discount"
                  big={false}
                />
              </div>
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-3">
                <StringInput
                  big={false}
                  type="number"
                  path="currentStockCount"
                  label="Ground Stock"
                />
              </div>
              <div className="col-span-3">
                <StringInput
                  disabled={true}
                  big={false}
                  type="number"
                  path="holdStockCount"
                  label="Hold Stock"
                />
              </div>
              <div className="col-span-3">
                <ControlGroup fill>
                  <StringInput
                    disabled={state?.relatedStock?.id ? true : false}
                    big={false}
                    type="number"
                    min={0}
                    step={5}
                    max={
                      state?.currentStockType?.id === "percentage"
                        ? 100
                        : undefined
                    }
                    label={
                      state?.currentStockType?.id === "percentage"
                        ? "% of Ground Stock"
                        : "Display Stock"
                    }
                    path={
                      state?.currentStockType?.id === "percentage"
                        ? "currentStockPercentage"
                        : "offerStockCount"
                    }
                  />
                  <StaticSelector
                    disabled={state?.relatedStock?.id ? true : false}
                    big={false}
                    label="Unit Type"
                    items={CURRENT_STOCK_UNITS}
                    path="currentStockType"
                  />
                </ControlGroup>
              </div>

              <div className="col-span-3">
                <CustomFormGroup
                  fill
                  label="Order Limit"
                  helperText="Min-Max (100 - 150)"
                >
                  <ControlGroup fill>
                    <StringInput
                      className="mb-0"
                      type="number"
                      big={false}
                      placeholder="Min"
                      path="orderingConditions.order_limit.0"
                    />
                    <StringInput
                      className="mb-0"
                      type="number"
                      big={false}
                      placeholder="Max"
                      path="orderingConditions.order_limit.1"
                    />
                  </ControlGroup>
                </CustomFormGroup>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6">
                <CustomFormGroup label="Ordering Conditions">
                  <div className="flex">
                    <CustomCheckbox
                      className="mr-3"
                      path="orderingConditions.is_pick_up"
                      label="Pick up"
                    />
                    <CustomCheckbox
                      className="mr-3"
                      path="orderingConditions.is_home_delivery"
                      label="Home delivery"
                    />
                    <CustomCheckbox
                      className="mr-3"
                      path="orderingConditions.is_cod_available"
                      label="Cash"
                    />{" "}
                    <CustomCheckbox
                      className="mr-3"
                      path="orderingConditions.is_online_payment_available"
                      label="Online"
                    />
                  </div>
                </CustomFormGroup>
              </div>
              {/* The below conditions are put to maintain layouts effectively under multiple combinations of conditional rendering */}
              {isReseller ? (
                <>
                  <div className="col-span-3">
                    <StringInput
                      leftIcon="rupee"
                      label="Min Rate"
                      path="minRate"
                      helperText="Minimum rate for resale"
                      big={false}
                    />
                  </div>
                  <div className="col-span-3">
                    <StringInput
                      leftIcon="rupee"
                      label="Max Rate"
                      path="maxRate"
                      helperText="Maximum rate for resale"
                      big={false}
                    />
                  </div>
                </>
              ) : (
                <>
                  <AlternateStockDropdown sellableItem={sellableItem} />
                  <div className="col-span-3">
                    <CustomDateInput
                      big={false}
                      path="expiryDate"
                      label="Expiry Date"
                    />
                  </div>
                </>
              )}
            </div>
            {isReseller ? (
              <div className="grid grid-cols-12 gap-4 mt-3">
                <AlternateStockDropdown sellableItem={sellableItem} />
                <div className="col-span-3">
                  <CustomDateInput
                    big={false}
                    path="expiryDate"
                    label="Expiry Date"
                  />
                </div>
              </div>
            ) : null}
            <hr className="my-0 mt-3" />
            <div className="flex justify-between mt-3">
              <DisplayStockNote stock={state} />
              <ButtonMutation
                disabled={disabled}
                validate={() => {
                  if (errors?.latest) {
                    AppToaster.show({
                      message: errors?.latest,
                      intent: "danger",
                      icon: "cross",
                    })
                    return false
                  }
                  return true
                }}
                icon={stock?.id ? "saved" : "add"}
                onCompleted={({ addUpdateSellableItemStock: asic }) => {
                  const item = asic?.sellableItemStock
                  AppToaster.show({
                    message: `Stock saved for item ${item?.sellableItem?.name} on  DC ${item?.distributionCentre.name}`,
                    intent: "success",
                    icon: "tick",
                  })
                  onCompleted(index, item)
                }}
                onError={error => {
                  AppToaster.show({
                    message: graphqlError(error.message),
                    intent: "danger",
                    icon: "cross",
                  })
                }}
                mutation={ADD_UPDATE_SELLABLE_ITEM_STOCK}
                variables={parsedVariables}
                text={stock?.id ? "Save" : "Add"}
              />
            </div>
          </section>
        </div>
        <ItemBenchmarkDialog
          sellableItem={sellableItem}
          isOpen={isOpen}
          onClose={() => {
            setOpen(false)
          }}
        />
      </FormController>
    </>
  )
}

export default SellableItemStock
